<template>
  <v-tooltip top :disabled="isReindexing || isRefreshing">
    <template v-slot:activator="{ on }">
      <div class="ingested-badge" v-on="on">
        <!-- Loading -->
        <v-progress-circular
          v-if="isLoading"
          :size="size"
          indeterminate
          :color="color"
          width="3"
        />

        <!-- Indexed -->
        <v-icon v-else-if="isIndexed" :color="color" :size="size">
          check_circle
        </v-icon>

        <!-- Not supported -->
        <v-icon v-else-if="isNotSupported" color="grey" :size="size">
          block
        </v-icon>

        <!-- Queued -->
        <v-btn
          v-else-if="isQueued"
          icon
          x-small
          :loading="isRefreshing"
          :disabled="isRefreshing"
          color="info"
          @click.stop="handleRefreshStatus"
        >
          <v-icon :size="size">sync</v-icon>
        </v-btn>

        <!-- Can reindex -->
        <v-btn
          v-else-if="canReindex"
          icon
          x-small
          :loading="isReindexing"
          :disabled="isReindexing"
          color="warning"
          @click.stop="handleReindex"
        >
          <v-icon :size="size">refresh</v-icon>
        </v-btn>
      </div>
    </template>

    <span>{{ tooltipText }}</span>
  </v-tooltip>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import api from '@/factories/api';

export default {
  name: 'IngestedBadge',

  props: {
    size: {
      type: [Number, String],
      default: 20,
    },
    color: {
      type: String,
      default: 'success',
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ['file', 'lesson'].includes(value),
    },
    id: {
      type: [String, Number],
      required: true,
    },
    lessonId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      isReindexing: false,
      isRefreshing: false,
      isQueued: false,
    };
  },

  computed: {
    ...mapGetters('files', {
      fileIngestionStatus: 'fileIngestionStatus',
    }),
    ...mapGetters({
      itemIngestionStatus: 'itemIngestionStatus',
    }),

    isFile() {
      return this.type === 'file';
    },

    ingestionStatus() {
      return this.isFile
        ? this.fileIngestionStatus(this.id)
        : this.itemIngestionStatus(this.id) || {};
    },

    ingested() {
      return this.ingestionStatus.ingested;
    },

    supported() {
      return this.ingestionStatus.supported;
    },

    isLoading() {
      return this.ingested == null && !this.isQueued;
    },

    isIndexed() {
      return this.ingested === true && this.supported === true;
    },

    isNotSupported() {
      return this.supported === false;
    },

    canReindex() {
      return (
        this.supported === true && this.ingested === false && !this.isQueued
      );
    },

    tooltipText() {
      if (this.isReindexing) return 'Queueing...';
      if (this.isRefreshing) return 'Checking status...';
      if (this.isQueued) return 'Queued — click to refresh';
      if (this.isLoading) return 'Loading status...';
      if (this.isIndexed) return 'Indexed';
      if (this.isNotSupported) return 'Not supported for search';
      if (this.canReindex) return 'Index for search';
      return 'Not indexed';
    },
  },

  watch: {
    ingested(val) {
      if (val != null) this.isQueued = false;
    },
    supported(val) {
      if (val === false) this.isQueued = false;
    },
    id(newVal, oldVal) {
      if (newVal !== oldVal && oldVal !== undefined) {
        this.resetLocalState();
      }
    },
  },

  methods: {
    ...mapActions('files', {
      loadFileIngestionStatus: 'loadIngestionStatus',
    }),
    ...mapActions({
      loadLessonItemsIngestionStatus: 'loadLessonItemsIngestionStatus',
    }),

    resetLocalState() {
      this.isQueued = false;
      this.isReindexing = false;
      this.isRefreshing = false;
    },

    async handleReindex() {
      if (!this.id) return;

      this.isReindexing = true;

      try {
        const endpoint = this.isFile ? '/files/reindex' : '/lessons/reindex';
        const payload = this.isFile
          ? { file_id: this.id }
          : { lesson_id: this.lessonId };

        await api().post(endpoint, payload);
        this.isQueued = true;
      } finally {
        this.isReindexing = false;
      }
    },

    async handleRefreshStatus() {
      if (!this.id) return;

      this.isRefreshing = true;

      try {
        if (this.isFile) {
          await this.loadFileIngestionStatus([this.id]);
        } else if (this.lessonId) {
          await this.loadLessonItemsIngestionStatus(this.lessonId);
        }
      } catch (err) {
        console.error('Failed to refresh ingestion status:', err);
      } finally {
        this.isRefreshing = false;
      }
    },
  },
};
</script>

<style scoped>
.ingested-badge {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
